import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Menubar } from 'primereact/menubar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import store from '../store/store';
import axiosAdmin from '../services/axiosAdmin';
import { readAcr122uUid } from '../services/acr122uService';
import { podeExecutarLimpeza } from '../pages/empresa/limpezaPeriodo/limpezaPeriodo';

import * as actions from '../store/modules/auth/actions';

export default function Navbar({ onNavigate, menuBackground }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isAuthorizingStock, setIsAuthorizingStock] = useState(false);
  const [showStockAuthorization, setShowStockAuthorization] = useState(false);
  const [typedUid, setTypedUid] = useState('');

  const logout = () => {
    dispatch(actions.loginFailure());

    navigate('/login');
  };

  const {
    id: idUsuario,
    tipoUsuario,
    tipoAcesso,
    modeloNegocio,
    moduloFinanceiro,
    operadorGrupoAcesso,
  } = store.getState().auth.user;

  const getAuthorizationError = error => {
    if (error instanceof TypeError && !error.response) {
      return 'O serviço local do leitor não está em execução. Execute C:\\react\\eloadmin\\acr122u-bridge\\iniciar-leitor.bat e tente novamente.';
    }
    const apiErrors = error.response && error.response.data && error.response.data.errors;
    return apiErrors && apiErrors.length
      ? apiErrors[0]
      : error.message || 'Não foi possível autorizar o acesso pelo cartão.';
  };

  const validateStockCredential = async (credencial, origemCartao = false) => {
    await axiosAdmin.post('/usuario/autorizar-atualiza-estoque', null, {
      params: { idUsuario, credencial, origemCartao },
    });

    setShowStockAuthorization(false);
    setTypedUid('');
    toast.success('Cartão autorizado.');
    navigate('/atualizaestoque');
  };

  const authorizeStockByReader = async () => {
    if (isAuthorizingStock) return;
    const toastId = 'acr122u-stock-authorization';
    setIsAuthorizingStock(true);
    toast.info('Aproxime o cartão do leitor ACR122U...', { autoClose: false, toastId });

    try {
      const uid = await readAcr122uUid();
      await validateStockCredential(uid, true);
      toast.dismiss(toastId);
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(getAuthorizationError(error));
    } finally {
      setIsAuthorizingStock(false);
    }
  };

  const authorizeStockByTypedUid = async () => {
    const normalizedUid = typedUid.trim();
    if (!normalizedUid) {
      toast.info('Informe a senha de administrador.');
      return;
    }
    if (isAuthorizingStock) return;

    setIsAuthorizingStock(true);
    try {
      await validateStockCredential(normalizedUid);
    } catch (error) {
      toast.error(getAuthorizationError(error));
    } finally {
      setIsAuthorizingStock(false);
    }
  };

  const authorizeStockUpdate = () => {
    setTypedUid('');
    setShowStockAuthorization(true);
  };

  const itemsAdmin = [
    {
      label: 'Início',
      icon: 'pi pi-fw pi-home',
      accessModule: 'Home',
      command: () => { navigate('/home'); },
    },
    {
      label: 'Tabelas',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Usuários',
          command: () => {
            navigate('/consultausuario');
          },
        },
        {
          separator: true,
        },
        {
          label: 'Empresas',
          command: () => { navigate('/consultaempresa'); },
        },
        {
          separator: true,
        },
        {
          label: 'Operador',
          command: () => { navigate('/consultaoperador'); },
        },
        {
          separator: true,
        },
        {
          label: 'Ramo de Negócio',
          command: () => { navigate('/consultaramonegocio'); },
        },
        {
          separator: true,
        },
        {
          label: 'Municípios',
          command: () => { navigate('/consultamunicipio'); },
        },
        {
          separator: true,
        },
        {
          label: 'Unidade da Federação',
          command: () => { navigate('/consultaunidadefederacao'); },
        },
        {
          separator: true,
        },
        {
          label: 'Bancos',
          command: () => { navigate('/consultabanco'); },
        },
        {
          separator: true,
        },
        {
          label: 'Modulo',
          command: () => { navigate('/admconsultamodulo'); },
        },
        {
          separator: true,
        },
        {
          label: 'Tabelas Fiscais',
          items: [
            {
              label: 'Atualizar Tabelas Fiscais',
              command: () => { navigate('/atualizatabelafiscal'); },
            },
            {
              separator: true,
            },
            {
              label: 'CST ICMS',
              command: () => { navigate('/consultacst'); },
            },
            {
              label: 'CFOP',
              command: () => { navigate('/consultacfop'); },
            },
            {
              label: 'CSOSN',
              command: () => { navigate('/consultacsosn'); },
            },
            {
              label: 'Modalidade de Cálculo BC do ICMS',
              command: () => { navigate('/consultaicmsmodalidadebc'); },
            },
            {
              label: 'Modalidade de Cálculo BC do ICMS ST',
              command: () => { navigate('/consultaicmsmodalidadebcst'); },
            },
            {
              label: 'NCM',
              command: () => { navigate('/consultancm'); },
            },
            {
              label: 'CEST',
              command: () => { navigate('/consultacest'); },
            },
            {
              label: 'Gênero de Produto',
              command: () => { navigate('/consultageneroproduto'); },
            },
            {
              label: 'Tipo de Produto',
              command: () => { navigate('/consultatipoproduto'); },
            },
            {
              label: 'Serviço',
              command: () => { navigate('/consultaservico'); },
            },
            {
              label: 'Origem da Mercadoria',
              command: () => { navigate('/consultaorigemmercadoria'); },
            },
            {
              label: 'CST IPI',
              command: () => { navigate('/consultaipi'); },
            },
            {
              label: 'CST PIS/COFINS',
              command: () => { navigate('/consultapiscofins'); },
            },
          ],
        },
      ],
    },
    {
      label: 'Recebimentos',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Gerar Recebimentos',
          command: () => {
            navigate('/gerarrecebimento');
          },
        },
        {
          label: 'Consulta Recebimentos em Aberto',
          command: () => {
            navigate('/consultarecebimentoemaberto');
          },
        },
        {
          separator: true,
        },
        {
          label: 'Remessa Boleto',
          command: () => {
            navigate('/remessaboleto');
          },
        },
        {
          separator: true,
        },
        {
          label: 'Configuração Shipay',
          command: () => {
            navigate('/configuracaoshipay');
          },
        },
      ],
    },
    {
      label: 'Relatórios',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Recebimentos',
          command: () => {
            navigate('/relatoriorecebimento');
          },
        },
      ],
    },
    {
      label: 'Tickets',
      icon: 'pi pi-fw pi-table',
      command: () => { navigate('/admconsultaticket'); },
    },
    {
      label: 'Sair',
      icon: 'pi pi-fw pi-power-off',
      command: () => { logout(); },
    },
  ];

  const itemsTabelaFinanceiro = {
    label: 'Financeiro',
    icon: 'pi pi-fw pi-table',
    items: [
      {
        label: 'Centro de Custo',
        command: () => { navigate('/consultacentrocusto'); },
      },
      {
        label: 'DRE Financeiro',
        command: () => { navigate('/consultafinanceirodre'); },
      },
      {
        label: 'Meio de Pagamento',
        command: () => { navigate('/consultameiopagamento'); },
      },
      {
        label: 'Conta Bancária',
        command: () => { navigate('/consultacontabancaria'); },
      },
      {
        label: 'Layout Número OFX',
        command: () => { navigate('/consultaofxlayoutnumero'); },
      },
    ],
  };

  const itemsTabelaEstamparia = {
    label: 'Estamparia',
    icon: 'pi pi-fw pi-table',
    items: [
      {
        label: 'Modelo',
        command: () => { navigate('/consultaestampariamodelo'); },
      },
      {
        label: 'Tamanho',
        command: () => { navigate('/consultaestampariatamanho'); },
      },
      {
        label: 'Malha',
        command: () => { navigate('/consultaestampariamalha'); },
      },
      {
        label: 'Cor',
        command: () => { navigate('/consultaestampariacor'); },
      },
      {
        label: 'Bolso',
        command: () => { navigate('/consultaestampariabolso'); },
      },
      {
        label: 'Gola',
        command: () => { navigate('/consultaestampariagola'); },
      },
      {
        label: 'Acabamento da Gola',
        command: () => { navigate('/consultaestampariagolaacabamento'); },
      },
      {
        label: 'Manga',
        command: () => { navigate('/consultaestampariamanga'); },
      },
      {
        label: 'Acabamento da Manga',
        command: () => { navigate('/consultaestampariamangaacabamento'); },
      },
      {
        label: 'Bordado',
        command: () => { navigate('/consultaestampariabordado'); },
      },
      {
        label: 'Silk',
        command: () => { navigate('/consultaestampariasilk'); },
      },
      {
        label: 'Sublimação',
        command: () => { navigate('/consultaestampariasublimacao'); },
      },
      {
        label: 'Acessório',
        command: () => { navigate('/consultaestampariaacessorio'); },
      },
      {
        separator: true,
      },
      {
        label: 'Ítem Padrão',
        command: () => { navigate('/consultaestampariaitempadrao'); },
      },
      {
        separator: true,
      },
      {
        label: 'Configuração dos Preços',
        command: () => { navigate('/estampariaPrecificacao'); },
      },
    ],
  };

  const itemsRelatorioEstamparia = {
    label: 'Estamparia',
    icon: 'pi pi-fw pi-table',
    items: [
      {
        label: 'Relatório Orçamentos x Pedidos',
        command: () => { navigate('/relatorioestampariaorcamentoxpedido'); },
      },
      {
        label: 'Relatório de Orçamentos',
        command: () => { navigate('/relatorioestampariaorcamento'); },
      },
      {
        label: 'Relatório de Ordem de Produção',
        command: () => { navigate('/relatorioestampariaordemproducao'); },
      },
      {
        label: 'Relatório de Pedidos',
        command: () => { navigate('/relatorioestampariapedido'); },
      },
      {
        label: 'Relatório de Pedidos - Financeiro',
        command: () => { navigate('/relatorioestampariapedidofinanceiro'); },
      },
      {
        label: 'Relatório de Serviços',
        command: () => { navigate('/relatorioestampariaservico'); },
      },
      {
        label: 'Relatório de Serviços em Aberto',
        command: () => { navigate('/relatorioestampariaservicopedidoemaberto'); },
      },
      {
        label: 'Relatório de Vendedores',
        items: [
          {
            label: 'Orçamentos',
            command: () => { navigate('/relatorioestampariaorcamentovendedores'); },
          },
          {
            label: 'Ordens de Produção',
            command: () => { navigate('/relatorioestampariaordemproducaovendedores'); },
          },
          {
            label: 'Pedidos',
            command: () => { navigate('/relatorioestampariapedidovendedores'); },
          },
        ],
      },
      {
        separator: true,
      },
      {
        label: 'Relatório de Caixa',
        command: () => { navigate('/relatorioestampariacaixa'); },
      },
      {
        label: 'Relatório Recebimentos em Aberto',
        command: () => { navigate('/relatorioestampariarecebimentopedidoaberto'); },
      },
    ],
  };

  const itemsEmpresa = [
    {
      icon: 'pi pi-fw pi-home',
      accessModule: 'Home',
      command: () => { navigate('/home'); },
    },
    {
      label: 'Tabelas',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Produtos',
          accessModule: 'ConsultaProduto',
          command: () => { navigate('/consultaproduto'); },
        },
        {
          label: 'Participantes',
          accessModule: 'ConsultaParticipante',
          command: () => { navigate('/consultaparticipante'); },
        },
        {
          label: 'Unidades de Medida',
          accessModule: 'ConsultaUnidadeMedida',
          command: () => { navigate('/consultaunidademedida'); },
        },
        {
          label: 'Grupos de Produtos',
          accessModule: 'ConsultaGrupo',
          command: () => { navigate('/consultagrupo'); },
        },
        {
          label: 'Sub-Grupos',
          accessModule: 'ConsultaSubGrupo',
          command: () => { navigate('/consultasubgrupo'); },
        },
        /* {
          label: 'Carteira Digital',
          command: () => { navigate('/consultacarteiradigital'); },
        }, */
        /* {
          label: 'Bandeira de Cartão',
          command: () => { navigate('/consultabandeiracartao'); },
        }, */
        /* {
          label: 'Administradora de Cartão',
          command: () => { navigate('/consultaadministradoracartao'); },
        }, */
        {
          label: 'Grupo de Acesso',
          accessModule: 'ConsultaGrupoAcesso',
          command: () => { navigate('/consultagrupoacesso'); },
        },
        {
          label: 'Operador',
          accessModule: 'ConsultaOperador',
          command: () => { navigate('/empconsultaoperador'); },
        },
        {
          label: 'Vendedor',
          accessModule: 'ConsultaVendedor',
          command: () => { navigate('/consultavendedor'); },
        },
        {
          label: 'Técnico',
          accessModule: 'ConsultaTecnico',
          command: () => { navigate('/consultatecnico'); },
        },
        {
          label: 'Oficina',
          accessModule: 'ConsultaOficina',
          command: () => { navigate('/consultaoficina'); },
        },
        {
          label: 'Motorista',
          accessModule: 'ConsultaMotorista',
          command: () => { navigate('/consultamotorista'); },
        },
        {
          label: 'Veículo',
          accessModule: 'ConsultaVeiculo',
          command: () => { navigate('/consultaveiculo'); },
        },
        {
          label: 'Tabelas Fiscais',
          icon: 'pi pi-fw pi-table',
          items: [
            {
              label: 'Alíquota de ICMS por UF',
              accessModule: 'ConsultaAliquotaIcmsUf',
              command: () => { navigate('/consultaaliquotaicmsuf'); },
            },
            {
              label: 'Conversão CFOP',
              accessModule: 'ConsultaConversaoCfop',
              command: () => { navigate('/consultaconversaocfop'); },
            },
            {
              label: 'Conversão CSOSN CST',
              accessModule: 'ConsultaConversaoCsosnCst',
              command: () => { navigate('/consultaconversaocsosncst'); },
            },
            {
              label: 'Conta Analítica Contabil',
              accessModule: 'ConsultaContaAnalitica',
              command: () => { navigate('/consultacontaanalitica'); },
            },
            {
              label: 'Natureza de Operação',
              accessModule: 'ConsultaNaturezaOperacao',
              command: () => { navigate('/consultanaturezaoperacao'); },
            },
          ],
        },
      ],
    },
    {
      label: 'Doc. Fiscais',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'CT-e Emissão Própria',
          icon: 'pi pi-fw pi-table',
          items: [
            {
              label: 'Digitação de CT-e',
              command: () => { navigate('/consultacte'); },
            },
            {
              label: 'Envio de CT-e',
              accessModule: 'EmissaoCTe',
              command: () => { navigate('/emissaocte'); },
            },
          ],
        },
        {
          separator: true,
        },
        /* {
          label: 'MDF-e ',
          icon: 'pi pi-fw pi-table',
          items: [
            {
              label: 'Digitação de MDF-e',
              accessModule: 'ConsultaMDFe',
              command: () => { navigate('/consultamdfe'); },
            },
            {
              label: 'Envio de MDF-e',
              accessModule: 'EmissaoMDFe',
              command: () => { navigate('/emissaomdfe'); },
            },
          ],
        },
        {
          separator: true,
        }, */
        {
          label: 'NF-e Emissão Própria',
          icon: 'pi pi-fw pi-table',
          items: [
            {
              label: 'Digitação de NF-e',
              accessModule: 'ConsultaNFePropria',
              command: () => { navigate('/consultanfepropria'); },
            },
            {
              label: 'Envio de NF-e',
              accessModule: 'EnvioNFePropria',
              command: () => { navigate('/envionfepropria'); },
            },
          ],
        },
        {
          label: 'NF-e de Entrada',
          icon: 'pi pi-fw pi-table',
          items: [
            {
              label: 'Digitação de NF-e',
              accessModule: 'ConsultaNFeTerceiro',
              command: () => { navigate('/consultanfeterceiro'); },
            },
          ],
        },
        /* {
          icon: 'pi pi-fw pi-table',
          label: 'NFe Controle de Eventos',
          accessModule: 'ConsultaEventoNFe',
          command: () => { navigate('/consultaeventonfe'); },
        },
        {
          separator: true,
        }, */
        {
          label: 'NFC-e',
          icon: 'pi pi-fw pi-table',
          items: [
            {
              label: 'Emissão NFC-e',
              accessModule: 'EmissaoNFCe',
              command: () => { navigate('/emissaonfce'); },
            },
            {
              label: 'Consulta NFC-e',
              accessModule: 'ConsultaNFCe',
              command: () => { navigate('/consultanfce'); },
            },
            {
              label: 'Monitor NFC-e',
              accessModule: 'MonitorNFCe',
              command: () => { navigate('/monitornfce'); },
            },
          ],
        },
        {
          separator: true,
        },
        {
          label: 'Arquivos Fiscais',
          icon: 'pi pi-fw pi-table',
          items: [
            {
              label: 'Gerar Arquivo Sintegra',
              accessModule: 'GerarSintegra',
              command: () => { navigate('/gerarsintegra'); },
            },
          ],
        },
        {
          separator: true,
        },
        {
          label: 'Exportação de XML',
          accessModule: 'ExportarXML',
          command: () => { navigate('/exportarxml'); },
        },
        {
          label: 'Inutilização NFe/NFCe',
          accessModule: 'CadastroInutilizacao',
          command: () => { navigate('/cadastroinutilizacao'); },
        },
        {
          label: 'Status do Serviço',
          icon: 'pi pi-fw pi-table',
          items: [
            {
              label: 'NF-e/NFC-e',
              accessModule: 'StatusServico',
              command: () => { navigate('/statusservico'); },
            },
            {
              label: 'CT-e',
              accessModule: 'StatusServicoCte',
              command: () => { navigate('/statusservicocte'); },
            },
            /* {
              label: 'MDF-e',
              accessModule: 'StatusServicoMdfe',
              command: () => { navigate('/statusservicomdfe'); },
            }, */
          ],
        },
      ],
    },

    {
      label: 'Financeiro',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Painel Financeiro',
          accessModule: 'PainelFinanceiro',
          command: () => { navigate('/painelfinanceiro'); },
        },
        {
          separator: true,
        },
        {
          label: 'Movimentação Financeira',
          accessModule: 'ConsultaMovimentacaoFinanceira',
          command: () => { navigate('/consultamovimentacaofinanceira'); },
        },
        {
          label: 'Previsão Financeira',
          accessModule: 'ConsultaPrevisaoFinanceira',
          command: () => { navigate('/consultaprevisaofinanceira'); },
        },
        {
          label: 'Movimentação Bancária',
          accessModule: 'ConsultaMovimentacaoBancaria',
          command: () => { navigate('/consultamovimentacaobancaria'); },
        },
        {
          label: 'Livro Caixa',
          accessModule: 'ConsultaLivroCaixa',
          command: () => { navigate('/consultalivrocaixa'); },
        },
        {
          label: 'Fluxo Caixa',
          accessModule: 'ConsultaFluxoCaixa',
          command: () => { navigate('/consultafluxocaixa'); },
        },
        {
          separator: true,
        },
        {
          label: 'Pagamento online', // shipay
          accessModule: 'Pagamento',
          command: () => { navigate('/pagamento'); },
        },
      ],
    },
    {
      label: 'Relatórios',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Relatório de Clientes',
          items: [
            {
              label: 'Aniversariantes',
              command: () => { navigate('/relatorioclienteaniversariante'); },
            },
          ],
        },
        {
          label: 'Relatório Movimentação Produto',
          accessModule: 'RelatorioMovimentacaoProduto',
          command: () => { navigate('/relatoriomovimentacaoproduto'); },
        },
        {
          label: 'Relatório de CTe',
          command: () => { navigate('/relatorioCTe'); },
        },
        {
          label: 'Relatório de NFe',
          command: () => { navigate('/relatorioNFe'); },
        },
        {
          label: 'Relatório Ordem Serviço Aparelho',
          accessModule: 'RelatorioOrdemServicoAparelho',
          command: () => { navigate('/relatorioosaparelho'); },
        },
      ],
    },
    {
      label: 'Consultas',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Fechamento de Caixa',
          accessModule: 'ConsultaFechamentoCaixa',
          command: () => { navigate('/consultafechamentocaixa'); },
        },
        {
          label: 'Resumo Diário DAV',
          accessModule: 'ConsultaResumoDav',
          command: () => { navigate('/consultaresumodav'); },
        },
        {
          label: 'Resumo Diário NFC-e',
          accessModule: 'ConsultaResumoNfce',
          command: () => { navigate('/consultaresumonfce'); },
        },
      ],
    },
    {
      label: 'Outros',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Configuração do Sistema',
          icon: 'pi pi-fw pi-cog',
          accessModule: 'ConfiguracaoSistema',
          command: () => { navigate('/configuracaosistema'); },
        },
        {
          label: 'Movimentação de Produto',
          icon: 'pi pi-fw pi-table',
          accessModule: 'ConsultaMovimentacaoProduto',
          command: () => { navigate('/consultamovimentacaoproduto'); },
        },
        {
          label: 'Ordem de Serviço - Aparelhos',
          icon: 'pi pi-fw pi-table',
          accessModule: 'ConsultaOrdemServicoAparelho',
          command: () => { navigate('/consultaordemservicoaparelho'); },
        },
        {
          label: 'Orçamento',
          icon: 'pi pi-fw pi-table',
          accessModule: 'ConsultaOrcamento',
          command: () => { navigate('/consultaorcamento'); },
        },
        /* {
          label: 'Ponto de Venda',
          icon: 'pi pi-fw pi-table',
          command: () => { navigate('/consultapontovenda'); },
        }, */
        {
          label: 'Atualiza Estoque',
          icon: 'pi pi-fw pi-table',
          accessModule: 'AtualizaEstoque',
          command: () => { authorizeStockUpdate(); },
        },
        // Somente o login da própria empresa (sem operador)
        ...(podeExecutarLimpeza() ? [{
          label: 'Limpeza de Tabelas',
          icon: 'pi pi-fw pi-trash',
          accessModule: 'LimpezaPeriodo',
          command: () => { navigate('/limpezaperiodo'); },
        }] : []),
        {
          label: 'Tickets',
          icon: 'pi pi-fw pi-table',
          accessModule: 'EmpresaConsultaTicket',
          command: () => { navigate('/empresaconsultaticket'); },
        },
        {
          label: 'Alterar Senha',
          icon: 'pi pi-fw pi-lock',
          accessModule: 'AlterarSenha',
          command: () => { navigate('/alterarsenha'); },
        },
      ],
    },
    {
      label: 'Sair',
      icon: 'pi pi-fw pi-power-off',
      accessModule: 'Home',
      command: () => { logout(); },
    },
  ];

  const itemsSuporte = [
    {
      label: 'Início',
      icon: 'pi pi-fw pi-home',
      command: () => { navigate('/home'); },
    },
    {
      label: 'Tabelas',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Modulo',
          command: () => { navigate('/consultamodulo'); },
        },
        {
          separator: true,
        },
        {
          label: 'Empresas',
          command: () => { navigate('/consultaempresasuporte', { state: { somenteLeitura: true } }); },
        },
        {
          separator: true,
        },
        {
          label: 'Operador',
          command: () => { navigate('/consultaoperador', { state: { somenteLeitura: true } }); },
        },
      ],
    },
    {
      label: 'Tickets',
      icon: 'pi pi-fw pi-table',
      accessModule: 'ConsultaTicket',
      command: () => { navigate('/consultaticket'); },
    },
    {
      label: 'Recebimentos',
      icon: 'pi pi-fw pi-table',
      command: () => { navigate('/suporteconsultarecebimentoemaberto'); },
    },
    {
      label: 'Sair',
      icon: 'pi pi-fw pi-power-off',
      accessModule: 'Home',
      command: () => { logout(); },
    },
  ];

  const itemsContador = [
    {
      label: 'Início',
      icon: 'pi pi-fw pi-home',
      command: () => { navigate('/home'); },
    },
    {
      label: 'Tabelas',
      icon: 'pi pi-fw pi-table',
      items: [
      ],
    },
    {
      label: 'Sair',
      icon: 'pi pi-fw pi-power-off',
      accessModule: 'Home',
      command: () => { logout(); },
    },
  ];

  const itemsVendasEstamparia = [
    {
      label: 'Início',
      icon: 'pi pi-fw pi-home',
      command: () => { navigate('/home'); },
    },
    {
      label: 'Caixa',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Reforço',
          command: () => { navigate('/consultaestampariacaixareforco'); },
        },
        {
          separator: true,
        },
        {
          label: 'Sangria',
          command: () => { navigate('/consultaestampariacaixasangria'); },
        },
        {
          separator: true,
        },
      ],
    },
    {
      label: 'Orçamentos',
      icon: 'pi pi-fw pi-table',
      command: () => { navigate('/consultaestampariaorcamento'); },
    },
    {
      label: 'Pedidos',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Digitação de Pedidos',
          command: () => { navigate('/consultaestampariapedido'); },
        },
        {
          separator: true,
        },
        {
          label: 'Recebimento de Pedidos',
          command: () => { navigate('/consultaestampariarecebimentopedido'); },
        },
      ],
    },
    {
      label: 'Ordem de Produção',
      icon: 'pi pi-fw pi-table',
      items: [
        {
          label: 'Gerar Ordem de Produção',
          command: () => { navigate('/gerarestampariaordemproducao'); },
        },
        {
          separator: true,
        },
        {
          label: 'Consultar Ordem de Produção',
          command: () => { navigate('/consultaestampariaordemproducao'); },
        },
      ],
    },
    {
      label: 'Sair',
      icon: 'pi pi-fw pi-power-off',
      command: () => { logout(); },
    },
  ];

  const { isLoggedIn } = store.getState().auth;

  const accessList = operadorGrupoAcesso && operadorGrupoAcesso.listaGrupoAcessoBody
    ? operadorGrupoAcesso.listaGrupoAcessoBody
    : [];
  const allowedModules = new Set(accessList.map(({ modulo }) => modulo));

  const removeInvalidSeparators = (items) => items.filter((item, index) => (
    !item.separator
    || (index > 0 && index < items.length - 1 && !items[index - 1].separator)
  ));

  const filterMenuByAccess = (items) => {
    const filteredItems = items.reduce((result, item) => {
      if (item.accessModule && item.accessModule !== 'Home' && !allowedModules.has(item.accessModule)) {
        return result;
      }

      if (item.items) {
        const childItems = removeInvalidSeparators(filterMenuByAccess(item.items));
        if (!childItems.some(childItem => !childItem.separator)) {
          return result;
        }
        result.push({ ...item, items: childItems });
        return result;
      }

      result.push(item);
      return result;
    }, []);

    return removeInvalidSeparators(filteredItems);
  };

  const prepareMenu = (items) => items.map((item) => {
    const { accessModule, ...menuItem } = item;
    return {
      ...menuItem,
      items: item.items ? prepareMenu(item.items) : undefined,
      command: item.command ? (event) => {
        item.command(event);
        onNavigate();
      } : undefined,
    };
  });

  const menuItens = () => {
    if (tipoUsuario === 'ADMINISTRADOR') {
      return itemsAdmin;
    }
    if (tipoUsuario === 'EMPRESA') {
      if (tipoAcesso === 'VENDAS' && modeloNegocio === 'ESTAMPARIA') {
        return itemsVendasEstamparia;
      }
      if (modeloNegocio === 'ESTAMPARIA') {
        itemsEmpresa[1].items.push(itemsTabelaEstamparia);
        itemsEmpresa[4].items.push(itemsRelatorioEstamparia);
      }
      if (moduloFinanceiro) {
        itemsEmpresa[1].items.push(itemsTabelaFinanceiro);
      }

      return itemsEmpresa;
    }
    if (tipoUsuario === 'SUPORTE') {
      itemsEmpresa[1].items.push(itemsSuporte);
      return itemsSuporte;
    }
    return itemsContador;
  };

  const stockAuthorizationFooter = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'nowrap', gap: '8px' }}>
      <Button
        type="button"
        label="Cancelar"
        icon="pi pi-times"
        className="p-button-text"
        disabled={isAuthorizingStock}
        onClick={() => setShowStockAuthorization(false)}
      />
      <Button
        type="button"
        label="Ler cartão"
        icon="pi pi-id-card"
        className="p-button-secondary"
        loading={isAuthorizingStock}
        onClick={authorizeStockByReader}
      />
      <Button
        type="button"
        label="Confirmar"
        icon="pi pi-check"
        loading={isAuthorizingStock}
        onClick={authorizeStockByTypedUid}
      />
    </div>
  );

  if (isLoggedIn) {
    const items = menuItens();
    const visibleItems = operadorGrupoAcesso ? filterMenuByAccess(items) : items;
    return (
      <>
        <Menubar model={prepareMenu(visibleItems)} className="elo-menubar" style={{ background: menuBackground }} />
        <Dialog
          header="Autorização para atualizar estoque"
          visible={showStockAuthorization}
          modal
          closable={!isAuthorizingStock}
          closeOnEscape={!isAuthorizingStock}
          footer={stockAuthorizationFooter}
          onHide={() => setShowStockAuthorization(false)}
          style={{ width: 'min(560px, 95vw)' }}
        >
          <p>Aproxime o cartão do leitor ou digite a senha de administrador.</p>
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">Senha administrador</span>
            <InputText
              type="password"
              autoFocus
              maxLength={250}
              value={typedUid}
              disabled={isAuthorizingStock}
              onChange={event => setTypedUid(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') authorizeStockByTypedUid();
              }}
            />
          </div>
        </Dialog>
      </>
    );
  }

  return (<div></div>);
}

Navbar.defaultProps = {
  onNavigate: () => {},
  menuBackground: '#153e3b',
};

Navbar.propTypes = {
  onNavigate: PropTypes.func,
  menuBackground: PropTypes.string,
};
